import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Book, CheckCircle, Loader2, ArrowRight, Gift, Shield, Clock, Star, 
  Phone, Mail, User, Sparkles, Calculator, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const ebookData = {
  'rv-buying-guide': {
    title: 'The Complete RV Buying Guide',
    subtitle: 'Everything You Need to Know Before Buying Your First RV',
    description: 'Make your RV purchase with confidence. This comprehensive guide covers everything from choosing the right RV type to negotiating the best deal.',
    color: 'blue',
    benefits: [
      'Compare all RV types: Class A, B, C, Travel Trailers, Fifth Wheels',
      'Understand true costs of ownership beyond the sticker price',
      'Learn what to look for during RV inspections',
      'Get insider tips on negotiating the best deal',
      'Financing options explained simply',
      'First-time buyer mistakes to avoid',
    ],
  },
  'rv-travel-guide': {
    title: 'The RV Travel & Camping Guide',
    subtitle: 'Your Roadmap to Amazing RV Adventures',
    description: 'From finding the best campgrounds to mastering boondocking, this guide will help you get the most out of your RV lifestyle.',
    color: 'green',
    benefits: [
      'Find the best campgrounds across America',
      'Master the art of boondocking (free camping)',
      'Essential gear and supplies checklist',
      'Route planning tips from experienced RVers',
      'Save money with membership programs',
      'Pet-friendly camping guide',
    ],
  }
};

const EbookSqueezePage = () => {
  const { ebookType } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dealerSlug = searchParams.get('dealer');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [dealer, setDealer] = useState(null);
  const [formStarted, setFormStarted] = useState(false);
  const [sessionId] = useState(() => `squeeze_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const ebook = ebookData[ebookType];

  // Track page view on mount
  useEffect(() => {
    trackPageView();
    if (dealerSlug) {
      fetchDealer();
    }
    
    // Track abandonment on page leave
    const handleBeforeUnload = () => {
      if (formStarted && !loading) {
        trackFormInteraction('form_abandoned');
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dealerSlug]);

  const trackPageView = async () => {
    try {
      await axios.post(`${API}/api/tracking/page-view`, {
        page: 'squeeze_page',
        ebook_type: ebookType,
        dealer_slug: dealerSlug,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
      });
    } catch (err) {
      console.error('Tracking error:', err);
    }
  };

  const trackFormInteraction = async (action, fieldName = null) => {
    try {
      await axios.post(`${API}/api/tracking/form-interaction`, {
        page: 'squeeze_page',
        action,
        ebook_type: ebookType,
        dealer_slug: dealerSlug,
        field_name: fieldName,
        session_id: sessionId,
      });
    } catch (err) {
      console.error('Tracking error:', err);
    }
  };

  const fetchDealer = async () => {
    try {
      const res = await axios.get(`${API}/api/dealer/by-slug/${dealerSlug}`);
      setDealer(res.data);
    } catch (err) {
      console.error('Error fetching dealer:', err);
    }
  };

  const handleChange = (e) => {
    // Track form started on first interaction
    if (!formStarted) {
      setFormStarted(true);
      trackFormInteraction('form_started');
    }
    
    // Track field interaction
    trackFormInteraction('field_filled', e.target.name);
    
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleStartQuiz = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic phone validation
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    
    // Track form submission
    await trackFormInteraction('form_submitted');
    
    try {
      // Save lead data before starting quiz
      await axios.post(`${API}/api/ebook/pre-register`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        ebook_type: ebookType,
        dealer_slug: dealerSlug,
      });

      // Store in session for after quiz completion
      sessionStorage.setItem('ebook_lead', JSON.stringify({
        ...formData,
        ebookType,
        dealerSlug,
      }));

      // Redirect to quiz with return URL
      const returnUrl = encodeURIComponent(`/ebook-success/${ebookType}${dealerSlug ? `?dealer=${dealerSlug}` : ''}`);
      navigate(`/quiz?returnUrl=${returnUrl}&source=ebook`);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
      setLoading(false);
    }
  };

  if (!ebook) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Ebook not found</h1>
          <p className="text-gray-600 mt-2">The requested ebook doesn't exist.</p>
        </div>
      </div>
    );
  }

  const isBlue = ebook.color === 'blue';
  const gradientClass = isBlue ? 'from-blue-600 to-indigo-700' : 'from-green-600 to-emerald-700';
  const lightBgClass = isBlue ? 'bg-blue-50' : 'bg-green-50';
  const textColorClass = isBlue ? 'text-blue-600' : 'text-green-600';
  const buttonClass = isBlue ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700';
  const borderClass = isBlue ? 'border-blue-200' : 'border-green-200';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className={`bg-gradient-to-br ${gradientClass} text-white`}>
        <div className="max-w-6xl mx-auto px-4 py-8 md:py-16">
          <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center">
            {/* Left - Content */}
            <div className="order-2 md:order-1">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <Gift size={18} className="md:w-5 md:h-5" />
                <span className="text-xs md:text-sm font-medium opacity-90">FREE WHEN YOU COMPLETE THE FORM</span>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 leading-tight">
                {ebook.title}
              </h1>
              <p className="text-base md:text-xl opacity-90 mb-4 md:mb-6">
                {ebook.subtitle}
              </p>
              <p className="text-sm md:text-lg opacity-80 mb-6 md:mb-8 hidden md:block">
                {ebook.description}
              </p>
              
              {/* Trust Badges - Desktop */}
              <div className="hidden md:flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm opacity-90">
                  <Shield size={16} />
                  <span>No spam, ever</span>
                </div>
                <div className="flex items-center gap-2 text-sm opacity-90">
                  <Clock size={16} />
                  <span>2-min form</span>
                </div>
                <div className="flex items-center gap-2 text-sm opacity-90">
                  <Star size={16} />
                  <span>5,000+ downloads</span>
                </div>
              </div>
            </div>

            {/* Right - Form Card */}
            <div className="order-1 md:order-2 bg-white rounded-2xl p-5 md:p-8 shadow-2xl text-gray-900">
              <div className="text-center mb-5 md:mb-6">
                <div className={`w-12 h-12 md:w-16 md:h-16 ${lightBgClass} rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4`}>
                  <Book className={textColorClass} size={24} />
                </div>
                <h2 className="text-lg md:text-xl font-bold">Get Your Free Guide</h2>
                <p className="text-gray-500 text-xs md:text-sm mt-1">Complete the form to unlock</p>
              </div>

              <form onSubmit={handleStartQuiz} className="space-y-3 md:space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">First Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <Input
                        type="text"
                        name="firstName"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="h-10 md:h-12 pl-9 text-sm md:text-base"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Last Name</label>
                    <Input
                      type="text"
                      name="lastName"
                      placeholder="Smith"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="h-10 md:h-12 text-sm md:text-base"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      type="email"
                      name="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-10 md:h-12 pl-9 text-sm md:text-base"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      className="h-10 md:h-12 pl-9 text-sm md:text-base"
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className={`w-full h-11 md:h-12 text-sm md:text-base font-semibold ${buttonClass}`}
                >
                  {loading ? (
                    <Loader2 className="animate-spin mr-2" size={18} />
                  ) : (
                    <>
                      Start Free Quiz & Get Guide
                      <ArrowRight className="ml-2" size={16} />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-[10px] md:text-xs text-gray-400 text-center mt-3 md:mt-4">
                By signing up, you agree to receive emails from {dealer?.business_name || 'RV Match AI'}. 
                Unsubscribe anytime.
              </p>
            </div>
          </div>
          
          {/* Trust Badges - Mobile */}
          <div className="flex md:hidden justify-center gap-4 mt-6 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs opacity-90">
              <Shield size={14} />
              <span>No spam</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs opacity-90">
              <Clock size={14} />
              <span>2-min form</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs opacity-90">
              <Star size={14} />
              <span>5,000+ downloads</span>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="max-w-4xl mx-auto px-4 py-10 md:py-16">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-4">How It Works</h2>
          <p className="text-gray-600 text-sm md:text-base">Get your free guide in 3 simple steps</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
          <div className="flex md:flex-col items-center md:text-center gap-4 md:gap-0">
            <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full ${lightBgClass} flex items-center justify-center shrink-0 md:mb-4`}>
              <span className={`text-xl md:text-2xl font-bold ${textColorClass}`}>1</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm md:text-base">Enter Your Info</h3>
              <p className="text-gray-500 text-xs md:text-sm mt-1">Tell us how to reach you</p>
            </div>
          </div>
          
          <div className="flex md:flex-col items-center md:text-center gap-4 md:gap-0">
            <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full ${lightBgClass} flex items-center justify-center shrink-0 md:mb-4`}>
              <span className={`text-xl md:text-2xl font-bold ${textColorClass}`}>2</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm md:text-base">Complete the Quiz</h3>
              <p className="text-gray-500 text-xs md:text-sm mt-1">2-minute RV matching quiz</p>
            </div>
          </div>
          
          <div className="flex md:flex-col items-center md:text-center gap-4 md:gap-0">
            <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full ${lightBgClass} flex items-center justify-center shrink-0 md:mb-4`}>
              <span className={`text-xl md:text-2xl font-bold ${textColorClass}`}>3</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm md:text-base">Get Your Guide + Matches</h3>
              <p className="text-gray-500 text-xs md:text-sm mt-1">Instant access to everything</p>
            </div>
          </div>
        </div>
      </div>

      {/* What You'll Get Section */}
      <div className={`${lightBgClass} py-10 md:py-16`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-4">What You'll Get</h2>
            <p className="text-gray-600 text-sm md:text-base">Free tools to help you buy smarter</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {/* Ebook */}
            <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
              <div className="flex items-start gap-3 md:gap-4">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br ${gradientClass} flex items-center justify-center shrink-0`}>
                  <Book className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm md:text-base">{ebook.title}</h3>
                  <p className="text-gray-500 text-xs md:text-sm mt-1">{ebook.subtitle}</p>
                </div>
              </div>
              <ul className="mt-4 space-y-2">
                {ebook.benefits.slice(0, 3).map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs md:text-sm text-gray-600">
                    <CheckCircle className={`${textColorClass} shrink-0 mt-0.5`} size={14} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* RV Matches */}
            <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shrink-0">
                  <Sparkles className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm md:text-base">Personalized RV Matches</h3>
                  <p className="text-gray-500 text-xs md:text-sm mt-1">AI-powered recommendations</p>
                </div>
              </div>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle className="text-purple-500 shrink-0 mt-0.5" size={14} />
                  <span>RVs matched to your lifestyle & budget</span>
                </li>
                <li className="flex items-start gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle className="text-purple-500 shrink-0 mt-0.5" size={14} />
                  <span>Local dealer inventory included</span>
                </li>
                <li className="flex items-start gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle className="text-purple-500 shrink-0 mt-0.5" size={14} />
                  <span>Save favorites to compare later</span>
                </li>
              </ul>
            </div>

            {/* Cost Calculator Tool */}
            <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm md:col-span-2">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0">
                  <Calculator className="text-white" size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-sm md:text-base">Free RV Cost Calculator</h3>
                  <p className="text-gray-500 text-xs md:text-sm mt-1">Know exactly what you'll spend</p>
                </div>
              </div>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle className="text-amber-500 shrink-0 mt-0.5" size={14} />
                  <span>Calculate true 5-year ownership costs</span>
                </li>
                <li className="flex items-start gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle className="text-amber-500 shrink-0 mt-0.5" size={14} />
                  <span>Include insurance, maintenance, storage & more</span>
                </li>
                <li className="flex items-start gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle className="text-amber-500 shrink-0 mt-0.5" size={14} />
                  <span>See monthly payment breakdowns</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="max-w-xl mx-auto px-4 py-10 md:py-16 text-center">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-4">Ready to Find Your Perfect RV?</h2>
        <p className="text-gray-600 text-sm md:text-base mb-6 md:mb-8">
          Complete the quiz and get instant access to your free guide.
        </p>
        <Button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`h-11 md:h-12 px-6 md:px-8 text-sm md:text-base ${buttonClass}`}
        >
          Start Free Quiz Now
          <ChevronRight className="ml-2" size={18} />
        </Button>
      </div>

      {/* Footer */}
      {dealer && (
        <div className="border-t border-gray-200 py-6 md:py-8">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-gray-500 text-xs md:text-sm">
              Brought to you by <strong className="text-gray-700">{dealer.business_name}</strong>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EbookSqueezePage;
